import { ReactElement } from "react";
import Ad from "@components/Advertisement/Ad";
import VerticalHero from "@components/ArticleCard/VerticalHero";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import type { ArticleDataObject } from "@typings/Cue";
import { HeroVariant, TagsVariant } from "@typings/enums";
import { VerticalName } from "@util/constant";
import { cn, getKickerFromArticleDataObject } from "@util/helpers";

export type TopStoriesRowProps = {
  testId?: string;
  articles: ArticleDataObject[];
  verticalName: VerticalName;
};

const verticalToUniqueNameMapping: {
  verticalName: VerticalName;
  uniqueName: string;
}[] = [
  { verticalName: "asean", uniqueName: "international_asean" },
  { verticalName: "ge", uniqueName: "international_global" },
  { verticalName: "garage", uniqueName: "startups-tech_startups" },
  { verticalName: "sme", uniqueName: "singapore_smes" },
];

export default function TopStoriesRow({
  testId,
  articles,
  verticalName,
}: TopStoriesRowProps): ReactElement {
  const topStoryOne = articles.slice(0, 1);
  const topStoriesThree = articles?.slice(1, 4);

  const sectionUniqueName =
    verticalToUniqueNameMapping.find(
      (item) => item.verticalName === verticalName
    )?.uniqueName || "";

  return (
    <>
      {articles.length > 0 ? (
        <>
          <Row rootClassName="mb-6" data-testid={testId}>
            <Column rootClassName={cn("w-full px-3", "lg:w-8/12")}>
              {topStoryOne.map((article) => {
                return (
                  <VerticalHero
                    key={article.id}
                    articleData={article}
                    verticalName={verticalName}
                    tagsVariant={TagsVariant.Inside}
                    heroVariant={HeroVariant.Main}
                    showCreateTime={false}
                    sectionUniqueName={sectionUniqueName}
                    kicker={
                      article?.kicker?.fields?.[0]?.value ||
                      getKickerFromArticleDataObject(article)
                    }
                    sectionLinkRootClassName="lg:text-white lg:visited:text-white lg:hover:text-white lg:focus:text-white"
                  />
                );
              })}
            </Column>

            <Column rootClassName={cn("w-full px-3", "lg:w-4/12")}>
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center py-6 lg:sticky lg:top-[120px] lg:py-0"
              />
            </Column>
          </Row>

          <Row rootClassName="lg:mb-24">
            <Column rootClassName="w-full flex flex-col lg:flex-row lg:gap-6">
              {topStoriesThree?.map(
                (article: ArticleDataObject, index: number) => {
                  return (
                    <div
                      key={article?.id ?? index}
                      className={cn(
                        "mb-4 w-full border-b border-gray-175 pb-4",
                        "lg:mb-0 lg:w-4/12 lg:border-none lg:pb-0"
                      )}
                    >
                      <VerticalHero
                        articleData={article}
                        heroVariant={HeroVariant.Secondary}
                        verticalName={verticalName}
                        tagsVariant={TagsVariant.Outside}
                        showCreateTime={false}
                        sectionUniqueName={sectionUniqueName}
                        kicker={
                          article?.kicker?.fields?.[0]?.value ||
                          getKickerFromArticleDataObject(article)
                        }
                        sectionLinkRootClassName="lg:text-white lg:visited:text-white lg:hover:text-white lg:focus:text-white"
                      />
                    </div>
                  );
                }
              )}
            </Column>
          </Row>
        </>
      ) : null}
    </>
  );
}
